import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import { IFHError } from '../../../../apis/HttpBaseApiWrapper';
import { MessagingAPI } from '../../../../apis/messaging/messaging.api';
import {
  Concern,
  ConcernThread,
  CreateConcernPayload,
  PaginatedMessagesResponse,
} from '../../../../apis/messaging/messaging.interfaces';
import { mockConcerns, mockConcernThread } from '../../../../__mocks__/concerns/concernsMock';
import { logger } from '../../../../utils/common';
import isAppWithMock from 'src/utils/isAppWithMock';

type TQueryError = IFHError<{ message: string }>;
type TMutationError = IFHError<{ message: string }>;

export const getConcernsKey = 'get-concerns';
export const getConcernThreadKey = 'get-concern-thread';

const withMockData = isAppWithMock();

export const useGetConcernsHook = (
  options?: UseQueryOptions<Concern[], TQueryError>
) => {
  const queryKey = [getConcernsKey];

  return useQuery<Concern[], TQueryError>({
    queryKey,
    queryFn: async () => {
      if (withMockData) return mockConcerns;
      const data = await MessagingAPI.getConcerns();
      return Array.isArray(data) ? data : [];
    },
    ...options,
  });
};

export const useGetConcernThreadHook = (
  concernId: string | null,
  params: { page?: number; limit?: number } = {},
  options?: UseQueryOptions<PaginatedMessagesResponse | ConcernThread, TQueryError>
) => {
  const queryKey = [getConcernThreadKey, concernId, params];

  return useQuery<PaginatedMessagesResponse | ConcernThread, TQueryError>({
    queryKey,
    queryFn: async () => {
      if (!concernId) throw new Error('concernId required');
      if (withMockData) return mockConcernThread;
      return MessagingAPI.getConcernThread(
        concernId,
        params.page ?? 1,
        params.limit ?? 50
      );
    },
    enabled: !!concernId && (options?.enabled !== false),
    ...options,
  });
};

export const useCreateConcernHook = (
  options?: UseMutationOptions<unknown, TMutationError, CreateConcernPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, TMutationError, CreateConcernPayload>({
    mutationFn: async (payload) => {
      if (withMockData) {
        logger.show_success('Concern raised successfully (mock)');
        return { id: `c-${Date.now()}`, ...payload };
      }
      return MessagingAPI.createConcern(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [getConcernsKey] });
    },
    ...options,
  });
};
